/**
 * Card database registry.
 *
 * The engine must not import card JSON, because the engine has to run in a
 * Worker, in Node for the simulator, and in tests against fixture cards. So it
 * reads definitions through a registered CardDb. src/data/index.ts registers
 * the real set on import, and tests register fixtures.
 *
 * The registry is process-global on purpose: it is fixed data, never match
 * state, so `reduce` stays pure with respect to GameState.
 */

import type { CardDb, CardDefinition, TokenDefinition } from './types';
import type { CardType, Cost, Faction, Keyword, Ability, Subtype } from './cardTypes';

let db: CardDb = {
  getCard: () => undefined,
  getToken: () => undefined,
};

export function registerCardDb(next: CardDb): void {
  db = next;
  profileCache.clear();
}

export function getCardDb(): CardDb {
  return db;
}

export function getCardDef(id: string): CardDefinition | undefined {
  return db.getCard(id);
}

export function getTokenDef(id: string): TokenDefinition | undefined {
  return db.getToken(id);
}

/**
 * The shared read model for cards and tokens. Combat, effects and filters all
 * work against this so a token and a card are handled by the same code.
 */
export interface ObjectProfile {
  name: string;
  faction: Faction;
  type: CardType;
  subtypes: Subtype[];
  cost: Cost & { x?: boolean };
  baseAttack: number;
  baseHealth: number;
  printedKeywords: Keyword[];
  abilities: Ability[];
  legendary: boolean;
  isToken: boolean;
  wallet?: CardDefinition['wallet'];
  upgrade?: CardDefinition['upgrade'];
  text: string;
}

const EMPTY_PROFILE: ObjectProfile = {
  name: 'Unknown Card',
  faction: 'neutral',
  type: 'character',
  subtypes: [],
  cost: {},
  baseAttack: 0,
  baseHealth: 0,
  printedKeywords: [],
  abilities: [],
  legendary: false,
  isToken: false,
  text: '',
};

/**
 * Profiles are derived purely from immutable card definitions, so they are
 * memoized by id. This matters: filters, statics and effective stats call
 * profileOf many times per action, and building a fresh object each time was
 * the single hottest allocation in the engine. The cache is cleared whenever a
 * new card database is registered.
 */
const profileCache = new Map<string, ObjectProfile>();

/** Resolve a card id or token id into the shared read model, memoized. */
export function profileOf(ref: { cardId?: string; tokenId?: string }): ObjectProfile {
  const key = ref.tokenId ? `t:${ref.tokenId}` : ref.cardId ? `c:${ref.cardId}` : '';
  if (key === '') return EMPTY_PROFILE;
  const cached = profileCache.get(key);
  if (cached) return cached;
  const built = buildProfile(ref);
  profileCache.set(key, built);
  return built;
}

function buildProfile(ref: { cardId?: string; tokenId?: string }): ObjectProfile {
  if (ref.tokenId) {
    const t = getTokenDef(ref.tokenId);
    if (!t) return { ...EMPTY_PROFILE, isToken: true };
    return {
      name: t.name,
      faction: t.faction,
      type: 'character',
      subtypes: t.subtypes,
      cost: {},
      baseAttack: t.attack,
      baseHealth: t.health,
      printedKeywords: t.keywords,
      abilities: t.abilities,
      legendary: false,
      isToken: true,
      text: t.text,
    };
  }
  if (ref.cardId) {
    const c = getCardDef(ref.cardId);
    if (!c) return EMPTY_PROFILE;
    return {
      name: c.name,
      faction: c.faction,
      type: c.type,
      subtypes: c.subtypes,
      cost: c.cost,
      baseAttack: c.attack ?? 0,
      baseHealth: c.health ?? 0,
      printedKeywords: c.keywords,
      abilities: c.abilities,
      legendary: c.legendary === true,
      isToken: false,
      wallet: c.wallet,
      upgrade: c.upgrade,
      text: c.text,
    };
  }
  return EMPTY_PROFILE;
}

/** Total printed gas cost, used by filters and ai value maths. */
export function totalCost(cost: Cost): number {
  return (
    (cost.generic ?? 0) +
    (cost.btc ?? 0) +
    (cost.eth ?? 0) +
    (cost.sol ?? 0) +
    (cost.bnb ?? 0) +
    (cost.xrp ?? 0)
  );
}
